// Builds QuickBooks Online invoice-import CSV files from the rows of
// public.support_billable_conversions_export (migration 099). Pure: no I/O.
//
// QuickBooks' importer (Settings > Import data > Invoices) needs InvoiceNo,
// Customer, InvoiceDate, DueDate and ItemAmount; rows sharing an InvoiceNo
// become lines of one invoice; one import takes at most 1,000 rows and 100
// invoices. Dates are
// MM/DD/YYYY (a US company). Every date here is a UTC day, the same days the
// auto-charge job charges on.
//
// Taxable is "N" on every line: a company with sales tax on rejects lines
// without it, and one with sales tax off leaves the column unmapped. Whether
// the service is taxable is accounting's decision; this file never charges tax.

import { escapeCsv } from "../../utils/formatters";

export const QUICKBOOKS_COLUMNS = [
  "InvoiceNo",
  "Customer",
  "InvoiceDate",
  "DueDate",
  "Terms",
  "Memo",
  "Item(Product/Service)",
  "ItemDescription",
  "ItemQuantity",
  "ItemRate",
  "ItemAmount",
  "Service Date",
  "Taxable",
];

export const MAX_ROWS_PER_FILE = 1000;
// QuickBooks also imports at most 100 invoices at a time ("Import multiple
// invoices at once"), whatever their row count.
export const MAX_INVOICES_PER_FILE = 100;

export const TERMS_DAYS = { "Due on receipt": 0, "Net 15": 15, "Net 30": 30 };

export const DEFAULT_EXPORT_OPTIONS = {
  grouping: "day", // "day": one invoice per advertiser per day; "week": per advertiser per week; "range": per advertiser
  lines: "conversion", // "conversion": one line per conversion; "day": per day per CPA; "week": per week per CPA
  item: "",
  terms: "Due on receipt",
  invoiceNumbers: "ours", // "ours": deterministic; "quickbooks": QuickBooks assigns them
};

const DAY_MS = 24 * 60 * 60 * 1000;

// Advertiser, day, id: the RPC pages by advertiser and id, and lines (weekly
// runs above all) are built in day order, so sorting is ours.
function byAdvertiserAndDay(a, b) {
  return a.advertiser_id - b.advertiser_id
    || a.export_day.localeCompare(b.export_day)
    || a.conversion_id - b.conversion_id;
}

function addDays(isoDay, days) {
  return new Date(Date.parse(`${isoDay}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

export function usDate(isoDay) {
  const [y, m, d] = isoDay.split("-");
  return `${m}/${d}/${y}`;
}

/** Weekly lines need weekly or whole-range invoices: a daily invoice holds one day. */
export function weekLinesAllowed(grouping) {
  return grouping !== "day";
}

function countLabel(count) {
  return `${count} billable ${count === 1 ? "conversion" : "conversions"}`;
}

// Weeks run Monday to Sunday (UTC days, like everything here).
function weekStart(isoDay) {
  const weekday = new Date(`${isoDay}T00:00:00Z`).getUTCDay(); // 0 is Sunday
  return addDays(isoDay, -((weekday + 6) % 7));
}

// YYMMDD keeps our invoice numbers within QuickBooks' 21 characters.
function shortDay(isoDay) {
  return isoDay.slice(2).replace(/-/g, "");
}

function cents(amount) {
  return Math.round(Number(amount || 0) * 100);
}

/** Total spend of the rows in cents, rounded per row exactly as the file is. */
export function totalCents(rows) {
  return rows.reduce((sum, row) => sum + cents(row.spend), 0);
}

export function money(centsValue) {
  return (centsValue / 100).toFixed(2);
}

// A cell starting with one of these runs as a formula when the file is opened
// in a spreadsheet; a leading quote makes it plain text.
export function neutralize(text) {
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

function describeConversion(row) {
  const orderValue = row.order_value == null ? "(none)" : Number(row.order_value).toFixed(2);
  return `Order ${row.order_id ?? "(none)"}, event ${row.event_timestamp ?? "(none)"}, order value ${orderValue}`;
}

function conversionLines(rows) {
  return rows.map((row) => {
    const amount = cents(row.spend);
    return { day: row.export_day, description: describeConversion(row), quantity: "1", rate: money(amount), amount };
  });
}

// One line per CPA (the conversion's spend) inside a period (a day or a week).
// The count is the quantity and the CPA the rate, so quantity times rate is the
// amount; the line is dated on its first day. Weeks keep runs: rows come in
// day order (byAdvertiserAndDay), so a CPA change starts a new line from the day it
// happens and each line's days stay a contiguous span. A day line has one day,
// so it takes every conversion at its CPA, in whatever order they came.
function cpaLines(rows, periodOf, describeDays, { runs }) {
  const lines = [];
  const byKey = new Map();
  let current = null;
  for (const row of rows) {
    const day = row.export_day;
    const rate = cents(row.spend);
    const key = `${periodOf(day)}|${rate}`;
    let line = runs ? (current?.key === key ? current : null) : byKey.get(key);
    if (!line) {
      line = { key, rate, first: day, last: day, count: 0 };
      lines.push(line);
      byKey.set(key, line);
    }
    current = line;
    line.last = day;
    line.count += 1;
  }
  return lines.map(({ rate, first, last, count }) => ({
    day: first,
    description: `${countLabel(count)} at $${money(rate)}${describeDays(first, last)}`,
    quantity: String(count),
    rate: money(rate),
    amount: count * rate,
  }));
}

const dayLines = (rows) => cpaLines(rows, (day) => day, (day) => ` on ${usDate(day)}`, { runs: false });

const weekLines = (rows) => cpaLines(rows, weekStart,
  (first, last) => `, ${usDate(first)}` + (last === first ? "" : ` to ${usDate(last)}`), { runs: true });

// The days an invoice covers, inside the range: one day, the part of a Monday
// to Sunday week in the range, or the whole range. It is dated on its last day.
function invoicePeriod(verdictDay, grouping, start, end) {
  if (grouping === "range") return { first: start, last: end };
  if (grouping === "week") {
    const monday = weekStart(verdictDay);
    const sunday = addDays(monday, 6);
    return { first: monday > start ? monday : start, last: sunday < end ? sunday : end };
  }
  return { first: verdictDay, last: verdictDay };
}

// Our number names the days the invoice covers, so a week cut by two exports
// gets two numbers, and a week or range never shares a daily number.
function ourNumber(advertiserId, { first, last }) {
  return first === last ? `${advertiserId}-${shortDay(first)}` : `${advertiserId}-${shortDay(first)}-${shortDay(last)}`;
}

// Invoices in a stable order: advertiser, then day. Each keeps its rows in
// byAdvertiserAndDay order.
function groupInvoices(rows, { start, end, grouping }) {
  const invoices = new Map();
  for (const row of rows) {
    const period = invoicePeriod(row.export_day, grouping, start, end);
    const key = `${row.advertiser_id}|${period.first}`;
    if (!invoices.has(key)) {
      invoices.set(key, {
        advertiserId: row.advertiser_id,
        customer: row.customer_name || `Advertiser ${row.advertiser_id}`,
        invoiceDay: period.last,
        ours: ourNumber(row.advertiser_id, period),
        rows: [],
      });
    }
    invoices.get(key).rows.push(row);
  }
  return [...invoices.values()].sort((a, b) => a.advertiserId - b.advertiserId || a.invoiceDay.localeCompare(b.invoiceDay));
}

function invoiceRows(invoice, invoiceNo, { lines, item, terms }) {
  const dueDay = addDays(invoice.invoiceDay, TERMS_DAYS[terms] ?? 0);
  const build = { day: dayLines, week: weekLines }[lines] ?? conversionLines;
  const built = build(invoice.rows);
  return built.map((line) => [
    invoiceNo,
    neutralize(invoice.customer),
    usDate(invoice.invoiceDay),
    usDate(dueDay),
    terms,
    `Audiohook advertiser ${invoice.advertiserId}`,
    item.trim(),
    neutralize(line.description),
    line.quantity,
    line.rate,
    money(line.amount),
    usDate(line.day),
    "N",
  ]);
}

function toCsv(rows) {
  return [QUICKBOOKS_COLUMNS, ...rows].map((cells) => cells.map(escapeCsv).join(",")).join("\r\n") + "\r\n";
}

/**
 * Returns [{ filename, content }], one per file of at most MAX_ROWS_PER_FILE
 * data rows and MAX_INVOICES_PER_FILE invoices. An invoice is never split across files (a second file would
 * import its own invoice under the same number). Throws when one invoice
 * alone has more lines than a file can hold.
 */
export function buildInvoiceCsvFiles(rows, options) {
  const opts = { ...DEFAULT_EXPORT_OPTIONS, ...options };
  if (opts.lines === "week" && !weekLinesAllowed(opts.grouping)) {
    throw new Error("One line per week needs weekly or whole-range invoices.");
  }
  // export_day is the day a row counts on (migration 105): a charged
  // conversion's charge day, else its billing day. Without it the dates would
  // be wrong, so refuse rather than guess.
  if (rows.some((row) => !row.export_day)) {
    throw new Error("This export needs a database update (migration 105). Ask engineering to apply it.");
  }
  const invoices = groupInvoices([...rows].sort(byAdvertiserAndDay), opts).map((invoice, i) =>
    invoiceRows(invoice, opts.invoiceNumbers === "quickbooks" ? String(i + 1) : invoice.ours, opts));

  const files = [];
  let current = [];
  let invoicesInFile = 0;
  for (const lines of invoices) {
    if (lines.length > MAX_ROWS_PER_FILE) {
      // Lines per day per CPA almost always fit; when they do not, daily
      // invoices do. Suggest only what is not chosen yet.
      const hint = opts.lines !== "day" ? " Choose one line per day per CPA."
        : opts.grouping !== "day" ? " Choose one invoice per advertiser per day." : "";
      throw new Error(
        `Invoice ${lines[0][0]} has ${lines.length} lines; QuickBooks imports at most ${MAX_ROWS_PER_FILE} per file.${hint}`);
    }
    if (current.length + lines.length > MAX_ROWS_PER_FILE || invoicesInFile === MAX_INVOICES_PER_FILE) {
      files.push(current);
      current = [];
      invoicesInFile = 0;
    }
    current.push(...lines);
    invoicesInFile += 1;
  }
  if (current.length) files.push(current);

  const base = `quickbooks-invoices-${opts.start}-to-${opts.end}`;
  return files.map((fileRows, i) => ({
    filename: files.length === 1 ? `${base}.csv` : `${base}-part-${i + 1}-of-${files.length}.csv`,
    content: toCsv(fileRows),
  }));
}
